import * as ipaddr from 'ipaddr.js';
import { parse as parseTld } from 'tldts';

export const DEFAULT_MAX_EXPANDED_CIDR_HOSTS = 1024;

type NetblockVersion = 4 | 6;

export type AssetValidationFailure =
  | 'EMPTY'
  | 'INVALID_FORMAT'
  | 'RESERVED_IP'
  | 'TOO_LONG';

export type AssetValidationResult<T extends string = string> =
  | { ok: true; value: T; version?: NetblockVersion }
  | { ok: false; reason: AssetValidationFailure; message: string };

const parseIpv4Address = (value: string): number | null => {
  const octets = value.split('.');

  if (octets.length !== 4) {
    return null;
  }

  let address = 0;

  for (const octet of octets) {
    if (!/^\d+$/.test(octet)) {
      return null;
    }

    const parsedOctet = Number(octet);

    if (
      !Number.isInteger(parsedOctet) ||
      parsedOctet < 0 ||
      parsedOctet > 255
    ) {
      return null;
    }

    address = address * 256 + parsedOctet;
  }

  return address;
};

const formatIpv4Address = (address: number): string =>
  [
    Math.floor(address / 256 ** 3) % 256,
    Math.floor(address / 256 ** 2) % 256,
    Math.floor(address / 256) % 256,
    address % 256,
  ].join('.');

export const parseIpv4Cidr = (
  cidr: string,
): { address: number; prefixLength: number } | null => {
  const parts = cidr.split('/');

  if (parts.length !== 2 || !/^\d+$/.test(parts[1])) {
    return null;
  }

  const address = parseIpv4Address(parts[0]);
  const prefixLength = Number(parts[1]);

  if (
    address === null ||
    !Number.isInteger(prefixLength) ||
    prefixLength < 0 ||
    prefixLength > 32
  ) {
    return null;
  }

  return { address, prefixLength };
};

export const isIpv4CidrSeed = (value: string): boolean =>
  parseIpv4Cidr(value) !== null;

export const isIpv4InCidrRange = ({
  ipAddress,
  cidrRange,
}: {
  ipAddress: string;
  cidrRange: string;
}): boolean => {
  const parsedIp = parseIpv4Address(ipAddress);
  const parsedRange = parseIpv4Cidr(cidrRange);

  if (parsedIp === null || parsedRange === null) {
    return false;
  }

  const hostBits = 32 - parsedRange.prefixLength;
  const blockSize = 2 ** hostBits;
  const networkStart = Math.floor(parsedRange.address / blockSize) * blockSize;
  const networkEnd = networkStart + blockSize - 1;

  return parsedIp >= networkStart && parsedIp <= networkEnd;
};

export const isIpv6InCidrRange = ({
  ipAddress,
  cidrRange,
}: {
  ipAddress: string;
  cidrRange: string;
}): boolean => {
  if (!ipaddr.IPv6.isValid(ipAddress)) {
    return false;
  }

  try {
    const [network, prefixLength] = ipaddr.parseCIDR(cidrRange);

    if (network.kind() !== 'ipv6') {
      return false;
    }

    const address = ipaddr.parse(ipAddress);

    if (address.kind() !== 'ipv6') {
      return false;
    }

    return address.match([network, prefixLength]);
  } catch {
    return false;
  }
};

export const splitIpv4Cidr = ({
  cidr,
  targetPrefix,
}: {
  cidr: string;
  targetPrefix: number;
}): string[] => {
  const parsed = parseIpv4Cidr(cidr);

  if (!parsed) {
    return [cidr];
  }

  if (
    !Number.isInteger(targetPrefix) ||
    targetPrefix < 0 ||
    targetPrefix > 32
  ) {
    return [cidr];
  }

  if (parsed.prefixLength >= targetPrefix) {
    return [cidr];
  }

  const subnetCount = 2 ** (targetPrefix - parsed.prefixLength);
  const subnetSize = 2 ** (32 - targetPrefix);
  const networkStart =
    Math.floor(parsed.address / 2 ** (32 - parsed.prefixLength)) *
    2 ** (32 - parsed.prefixLength);

  return Array.from(
    { length: subnetCount },
    (_, index) =>
      `${formatIpv4Address(networkStart + index * subnetSize)}/${targetPrefix}`,
  );
};

const invalidResult = ({
  reason,
  message,
}: {
  reason: AssetValidationFailure;
  message: string;
}): AssetValidationResult => ({ ok: false, reason, message });

const validResult = ({
  value,
  version,
}: {
  value: string;
  version?: NetblockVersion;
}): AssetValidationResult => ({
  ok: true,
  value,
  ...(version ? { version } : {}),
});

const normalizeIpv6NetworkBytes = ({
  bytes,
  prefixLength,
}: {
  bytes: number[];
  prefixLength: number;
}): number[] => {
  const networkBytes = [...bytes];

  for (let bitIndex = prefixLength; bitIndex < 128; bitIndex += 1) {
    const byteIndex = Math.floor(bitIndex / 8);
    const bitMask = 1 << (7 - (bitIndex % 8));

    networkBytes[byteIndex] &= ~bitMask;
  }

  return networkBytes;
};

const getIpv6RangeEndpoints = ({
  address,
  prefixLength,
}: {
  address: ipaddr.IPv6;
  prefixLength: number;
}): { network: ipaddr.IPv6; last: ipaddr.IPv6 } => {
  const inputBytes = address.toByteArray();
  const networkBytes = normalizeIpv6NetworkBytes({
    bytes: inputBytes,
    prefixLength,
  });
  const lastBytes = [...networkBytes];

  for (let bitIndex = prefixLength; bitIndex < 128; bitIndex += 1) {
    const byteIndex = Math.floor(bitIndex / 8);
    const bitMask = 1 << (7 - (bitIndex % 8));

    lastBytes[byteIndex] |= bitMask;
  }

  const network = ipaddr.fromByteArray(networkBytes);
  const last = ipaddr.fromByteArray(lastBytes);

  if (network.kind() !== 'ipv6' || last.kind() !== 'ipv6') {
    throw new Error('Unexpected IPv6 range conversion result');
  }

  return {
    network: network as ipaddr.IPv6,
    last: last as ipaddr.IPv6,
  };
};

const normalizeInputToCidr = ({ input }: { input: string }): string | null => {
  if (input.includes('/')) {
    return input;
  }

  if (!input.includes(':') && parseIpv4Address(input) !== null) {
    return `${input}/32`;
  }

  if (input.includes(':') && ipaddr.IPv6.isValid(input)) {
    return `${input}/128`;
  }

  return null;
};

const ipv6ToBigInt = ({ address }: { address: ipaddr.IPv6 }): bigint =>
  address.toByteArray().reduce((acc, byte) => (acc << 8n) + BigInt(byte), 0n);

const bigIntToIpv6 = ({ value }: { value: bigint }): ipaddr.IPv6 => {
  const bytes = Array.from({ length: 16 }, (_, index) => {
    const shift = BigInt((15 - index) * 8);

    return Number((value >> shift) & 0xffn);
  });
  const parsed = ipaddr.fromByteArray(bytes);

  if (parsed.kind() !== 'ipv6') {
    throw new Error('Expected IPv6 address');
  }

  return parsed as ipaddr.IPv6;
};

export const splitIpv6Cidr = ({
  cidr,
  targetPrefix,
}: {
  cidr: string;
  targetPrefix: number;
}): string[] => {
  const [baseAddress, prefixString] = cidr.split('/');

  if (!baseAddress || !prefixString) {
    return [cidr];
  }

  if (!ipaddr.IPv6.isValid(baseAddress)) {
    return [cidr];
  }

  const prefix = Number(prefixString);

  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 128) {
    return [cidr];
  }

  if (
    !Number.isInteger(targetPrefix) ||
    targetPrefix < 0 ||
    targetPrefix > 128
  ) {
    return [cidr];
  }

  if (prefix >= targetPrefix) {
    return [cidr];
  }

  const parsedAddress = ipaddr.parse(baseAddress);

  if (parsedAddress.kind() !== 'ipv6') {
    return [cidr];
  }

  const ipv6Address = parsedAddress as ipaddr.IPv6;
  const networkStart =
    (ipv6ToBigInt({ address: ipv6Address }) >> BigInt(128 - prefix)) <<
    BigInt(128 - prefix);
  const subnetCount = 1n << BigInt(targetPrefix - prefix);

  if (subnetCount > BigInt(Number.MAX_SAFE_INTEGER)) {
    return [cidr];
  }

  const subnetSize = 1n << BigInt(128 - targetPrefix);

  const subnets: string[] = [];

  for (let i = 0n; i < subnetCount; i += 1n) {
    const subnetAddress = bigIntToIpv6({
      value: networkStart + i * subnetSize,
    });

    subnets.push(`${subnetAddress.toString()}/${targetPrefix}`);
  }

  return subnets;
};

type ParsedCidr = {
  cidr: string;
  address: ipaddr.IPv4 | ipaddr.IPv6;
  prefix: number;
  kind: 'ipv4' | 'ipv6';
};

const parseCidr = (cidr: string): ParsedCidr | null => {
  try {
    const [address, prefix] = ipaddr.parseCIDR(cidr);

    return {
      cidr,
      address,
      prefix,
      kind: address.kind(),
    };
  } catch {
    return null;
  }
};

export const isHostSizedCidr = (cidr: string): boolean => {
  const parsed = parseCidr(cidr);

  if (!parsed) {
    return false;
  }

  return parsed.kind === 'ipv4' ? parsed.prefix === 32 : parsed.prefix === 128;
};

const cidrContains = ({
  parent,
  child,
}: {
  parent: ParsedCidr;
  child: ParsedCidr;
}): boolean => {
  if (parent.kind !== child.kind || child.prefix < parent.prefix) {
    return false;
  }

  return child.address.match(parent.address, parent.prefix);
};

export const cidrIsCoveredBy = ({
  cidr,
  coveringCidrs,
}: {
  cidr: string;
  coveringCidrs: readonly string[];
}): boolean => {
  const child = parseCidr(cidr);

  if (!child) {
    return false;
  }

  return coveringCidrs.some((coveringCidr) => {
    const parent = parseCidr(coveringCidr);

    if (!parent) {
      return false;
    }

    return cidrContains({ parent, child });
  });
};

export const PRIVATE_SUBNET_ROOTS = [
  '10.0.0.0/8',
  '172.16.0.0/12',
  '192.168.0.0/16',
] as const;

export const isPrivateSubnetRoot = (cidr: string): boolean =>
  (PRIVATE_SUBNET_ROOTS as readonly string[]).includes(cidr);

export const prefixLength = (cidr: string): number => {
  const suffix = cidr.split('/')[1];

  if (!suffix) {
    return cidr.includes(':') ? 128 : 32;
  }

  const parsed = Number(suffix);

  return Number.isInteger(parsed) ? parsed : 0;
};

export const longestContainingCidr = ({
  cidr,
  candidates,
}: {
  cidr: string;
  candidates: readonly string[];
}): string | null => {
  const matches = candidates.filter(
    (candidate) =>
      candidate !== cidr &&
      cidrIsCoveredBy({ cidr, coveringCidrs: [candidate] }),
  );

  if (matches.length === 0) {
    return null;
  }

  return (
    [...matches].sort(
      (left, right) => prefixLength(right) - prefixLength(left),
    )[0] ?? null
  );
};

export const collapseContainedCidrs = ({
  cidrs,
}: {
  cidrs: string[];
}): string[] => {
  const unique = [
    ...new Set(
      cidrs.map((cidr) => {
        const trimmed = cidr.trim();

        return (
          normalizeInputToCidr({ input: trimmed }) ?? trimmed
        ).toLowerCase();
      }),
    ),
  ];
  const parsed = unique
    .map((cidr) => parseCidr(cidr))
    .filter((item): item is ParsedCidr => item !== null)
    .sort(
      (left, right) =>
        left.prefix - right.prefix || left.cidr.localeCompare(right.cidr),
    );

  const kept: ParsedCidr[] = [];

  for (const candidate of parsed) {
    const contained = kept.some((parent) =>
      cidrContains({ parent, child: candidate }),
    );

    if (!contained) {
      kept.push(candidate);
    }
  }

  return kept.map((item) => item.cidr);
};

export const expandIpv4Cidr = ({
  cidr,
  maxExpandedHosts = DEFAULT_MAX_EXPANDED_CIDR_HOSTS,
}: {
  cidr: string;
  maxExpandedHosts?: number;
}): string[] | null => {
  const parsed = parseIpv4Cidr(cidr);

  if (!parsed) {
    return null;
  }

  const addressCount = 2 ** (32 - parsed.prefixLength);
  const networkAddress =
    Math.floor(parsed.address / addressCount) * addressCount;
  const firstHost =
    parsed.prefixLength >= 31 ? networkAddress : networkAddress + 1;
  const lastHost =
    parsed.prefixLength >= 31
      ? networkAddress + addressCount - 1
      : networkAddress + addressCount - 2;
  const hostCount = lastHost >= firstHost ? lastHost - firstHost + 1 : 0;

  if (hostCount > maxExpandedHosts) {
    return [];
  }

  return Array.from({ length: hostCount }, (_, index) =>
    formatIpv4Address(firstHost + index),
  );
};

export const normalizeTargetRangesForDns = ({
  targetDomains,
  targetRanges,
  maxExpandedHosts = DEFAULT_MAX_EXPANDED_CIDR_HOSTS,
}: {
  targetDomains: string[];
  targetRanges: string[];
  maxExpandedHosts?: number;
}): string[] => {
  const targetSet = new Set<string>();

  for (const targetDomain of targetDomains) {
    const target = targetDomain.trim();

    if (target) {
      targetSet.add(target);
    }
  }

  for (const seedIpRange of targetRanges) {
    const target = seedIpRange.trim();

    if (!target) {
      continue;
    }

    const expandedTargets = expandIpv4Cidr({
      cidr: target,
      maxExpandedHosts,
    });

    if (expandedTargets === null) {
      if (!target.includes('/')) {
        targetSet.add(target);
      }

      continue;
    }

    for (const expandedTarget of expandedTargets) {
      targetSet.add(expandedTarget);
    }
  }

  return [...targetSet];
};

export const validateDomain = (input: string): AssetValidationResult => {
  const trimmedInput = input.trim().toLowerCase();
  const normalizedInput = trimmedInput.endsWith('.')
    ? trimmedInput.slice(0, -1)
    : trimmedInput;

  if (!normalizedInput) {
    return invalidResult({
      reason: 'EMPTY',
      message: 'Domain cannot be empty',
    });
  }

  if (normalizedInput.length > 253) {
    return invalidResult({
      reason: 'TOO_LONG',
      message: 'Domain is too long',
    });
  }

  if (
    /\s/.test(normalizedInput) ||
    normalizedInput.includes('/') ||
    normalizedInput.includes('?') ||
    normalizedInput.includes('#') ||
    normalizedInput.includes('://')
  ) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain has an invalid format',
    });
  }

  if (!normalizedInput.includes('.')) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain must contain a dot',
    });
  }

  const labels = normalizedInput.split('.');

  if (
    labels.some(
      (label) =>
        label.length === 0 ||
        label.length > 63 ||
        !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label),
    )
  ) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain has an invalid format',
    });
  }

  const parsed = parseTld(normalizedInput, { allowPrivateDomains: false });

  if (
    parsed.publicSuffix === null ||
    parsed.isIcann !== true ||
    parsed.domain === null
  ) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain must have a public TLD',
    });
  }

  return validResult({ value: normalizedInput });
};

export const validateInternalNetworkDomain = (
  input: string,
): AssetValidationResult => {
  const trimmedInput = input.trim().toLowerCase();
  const normalizedInput = trimmedInput.endsWith('.')
    ? trimmedInput.slice(0, -1)
    : trimmedInput;

  if (!normalizedInput) {
    return invalidResult({
      reason: 'EMPTY',
      message: 'Domain cannot be empty',
    });
  }

  if (normalizedInput.length > 253) {
    return invalidResult({
      reason: 'TOO_LONG',
      message: 'Domain is too long',
    });
  }

  if (
    /\s/.test(normalizedInput) ||
    normalizedInput.includes('/') ||
    normalizedInput.includes('?') ||
    normalizedInput.includes('#') ||
    normalizedInput.includes('://')
  ) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain has an invalid format',
    });
  }

  if (!normalizedInput.includes('.')) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain must contain a dot',
    });
  }

  const labels = normalizedInput.split('.');

  if (
    labels.some(
      (label) =>
        label.length === 0 ||
        label.length > 63 ||
        !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label),
    )
  ) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Domain has an invalid format',
    });
  }

  const parsed = parseTld(normalizedInput, { allowPrivateDomains: true });

  if (parsed.domain !== null) {
    return validResult({ value: normalizedInput });
  }

  if (labels.length >= 2) {
    return validResult({ value: normalizedInput });
  }

  return invalidResult({
    reason: 'INVALID_FORMAT',
    message: 'Domain has an invalid format',
  });
};

const isPublicUnicastRange = (range: string) => range === 'unicast';

const isInternalNetblockRange = (range: string) =>
  range === 'unicast' || range === 'private';

const validateNetblockWithRangePolicy = ({
  input,
  isRangeAllowed,
  reservedMessage,
}: {
  input: string;
  isRangeAllowed: (range: string) => boolean;
  reservedMessage: string;
}): AssetValidationResult => {
  const normalizedInput = input.trim().toLowerCase();

  if (!normalizedInput) {
    return invalidResult({
      reason: 'EMPTY',
      message: 'Netblock cannot be empty',
    });
  }

  const cidrInput = normalizeInputToCidr({ input: normalizedInput });

  if (!cidrInput) {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Netblock has an invalid format',
    });
  }

  let parsedCidr: [ipaddr.IPv4 | ipaddr.IPv6, number];

  if (cidrInput.includes('/')) {
    const [address] = cidrInput.split('/');

    if (address !== undefined && !address.includes(':')) {
      if (parseIpv4Address(address) === null) {
        return invalidResult({
          reason: 'INVALID_FORMAT',
          message: 'Netblock has an invalid format',
        });
      }
    }
  }

  try {
    parsedCidr = ipaddr.parseCIDR(cidrInput);
  } catch {
    return invalidResult({
      reason: 'INVALID_FORMAT',
      message: 'Netblock has an invalid format',
    });
  }

  const [address, prefixLength] = parsedCidr;

  if (address.kind() === 'ipv4') {
    const network = ipaddr.IPv4.networkAddressFromCIDR(
      `${address.toString()}/${prefixLength}`,
    );
    const broadcast = ipaddr.IPv4.broadcastAddressFromCIDR(
      `${address.toString()}/${prefixLength}`,
    );

    if (
      !isRangeAllowed(network.range()) ||
      !isRangeAllowed(broadcast.range())
    ) {
      return invalidResult({
        reason: 'RESERVED_IP',
        message: reservedMessage,
      });
    }

    return validResult({
      value: `${network.toString()}/${prefixLength}`,
      version: 4,
    });
  }

  const { network, last } = getIpv6RangeEndpoints({
    address: address as ipaddr.IPv6,
    prefixLength,
  });

  if (!isRangeAllowed(network.range()) || !isRangeAllowed(last.range())) {
    return invalidResult({
      reason: 'RESERVED_IP',
      message: reservedMessage,
    });
  }

  return validResult({
    value: `${network.toString()}/${prefixLength}`,
    version: 6,
  });
};

export const validateNetblock = (input: string): AssetValidationResult =>
  validateNetblockWithRangePolicy({
    input,
    isRangeAllowed: isPublicUnicastRange,
    reservedMessage: 'Reserved or private netblocks are not allowed',
  });

export const validateInternalNetblock = (
  input: string,
): AssetValidationResult =>
  validateNetblockWithRangePolicy({
    input,
    isRangeAllowed: isInternalNetblockRange,
    reservedMessage: 'Reserved netblocks are not allowed',
  });

export const hostAddressFromNetblock = (netblock: string): string => {
  const slashIndex = netblock.indexOf('/');

  if (slashIndex === -1) {
    return netblock;
  }

  const hostPart = netblock.slice(0, slashIndex);

  try {
    return ipaddr.parse(hostPart).toString();
  } catch {
    return hostPart;
  }
};

export const hostAddressesFromNetblocks = (
  netblocks: readonly string[],
): string[] => netblocks.map(hostAddressFromNetblock);

export const partitionAssets = <T extends string>({
  values,
  validator,
}: {
  values: string[];
  validator: (value: string) => AssetValidationResult<T>;
}): {
  valid: T[];
  invalid: { value: string; reason: AssetValidationFailure; message: string }[];
} => {
  const validSet = new Set<T>();
  const invalid: {
    value: string;
    reason: AssetValidationFailure;
    message: string;
  }[] = [];

  for (const value of values) {
    const result = validator(value);

    if (result.ok) {
      validSet.add(result.value);
      continue;
    }

    invalid.push({
      value,
      reason: result.reason,
      message: result.message,
    });
  }

  return {
    valid: [...validSet],
    invalid,
  };
};

export function normalizeHostIpAddress(ipAddress: string): string {
  const trimmed = ipAddress.trim();

  try {
    return ipaddr.parse(trimmed).toString();
  } catch {
    return trimmed.toLowerCase();
  }
}

type HostWithIpAddress = {
  id: string;
  ipAddress: string;
};

export function pickCanonicalHostsByIp<T extends HostWithIpAddress>(
  hosts: T[],
): T[] {
  const byIp = new Map<string, T>();

  for (const host of hosts) {
    const key = normalizeHostIpAddress(host.ipAddress);

    if (!byIp.has(key)) {
      byIp.set(key, host);
    }
  }

  return [...byIp.values()];
}
